export const workspacesArticle = {
    id: 'object-card',
    title: 'Паспорт объекта',
    category: 'Основы',
    requiredModule: null,
    summary: 'Настройка параметров объекта: 5 вкладок, приватность данных, быстрые действия и публичная витрина.',
    markdown: `
# Паспорт объекта (Окно свойств)

> **Паспорт объекта** — это карточка участка, здания, дороги или метки. Вся информация об объекте: от точной площади и цвета на карте до фотографий, документов и контактов собственника собрана в одном месте.

---

## 1. Как устроен паспорт: анатомия экрана и 5 закладок

Паспорт объекта состоит из плавающей мини-панели быстрых действий на карте и основной шторки с вкладками:

:::wireframe-passport

:::grid cols=2
:::card icon="edit-3" title="❶ Быстрые действия (слева)"
Мини-панель инструментов прямо у объекта:
* ✏️ **Правка геометрии:** перерисовать вершины и границы контура.
* 🔄 **Кнопки модулей:** быстрый доступ к функциям из подключаемых модулей.
* 🗑️ **Удаление объекта:** безопасное удаление с подтверждением.
:::

:::card icon="file-text" title="❷ Вкладка «Основное»"
Главные параметры участка или дома:
* **Название и кадастровый номер:** например, *«Участок № 15»* или *«Главный КПП»*.
* **Категория из легенды:** окрашивает объект в нужный цвет, штриховку или присваивает иконку.
* **Размеры:** площадь в м² (считается автоматически при черчении) и высота здания в метрах (для 3D-домов).
:::

:::card icon="lock" title="❸ Вкладка «Владение» (🔒 Служебная)"
**Строго закрытая информация:**
* **Собственник:** ФИО владельца или название компании, контакты.
* **Таблица арендаторов:** список организаций, которые снимают площадь, и сколько метров они занимают.
> 🔒 Данные этой вкладки **никогда не показываются** посетителям на публичной карте.
:::

:::card icon="tag" title="❹ Вкладка «Статусы»"
Цветные переключатели готовности лота:
* 🟢 **В продаже** — участок свободен и доступен для покупки.
* 🟡 **В строительстве** — на площадке идут работы.
* 🔵 **Запланировано** — перспективная очередь.
При включении этих флажков на карте появляются цветные значки, а клиенты могут находить участки через фильтры в легенде.
:::

:::card icon="image" title="❺ Вкладка «Медиа»"
Визуальные материалы для покупателей:
* **Фотогалерея:** снимки местности, видовые точки и фото хода стройки с короткими подписями.
* **Кнопки-ссылки:** ссылки на сайт поселка, буклет, презентацию или видеоролик.
:::

:::card icon="sliders" title="❻ Вкладка «Доп. инфо»"
* **Подробное описание:** любой пояснительный текст (например, правила застройки или особенности ландшафта).
* **Конструктор характеристик («Параметр — Значение»):** добавляйте любые нужные пункты в один клик: *«Электричество: 15 кВт»*, *«Газ: по границе участка»*, *«Вид права: Собственность»*.
:::
:::

---

## 2. Безопасность данных: что видит покупатель, а что скрыто

Чтобы случайно не раскрыть клиентам личные телефоны или финансовые условия, система строго разделяет поля:

| Вкладка в админке | Поле | Видно клиентам на сайте? | Как отображается у покупателя |
| :--- | :--- | :---: | :--- |
| **1. Основное** | Название | 👁️ **Видно** | Крупный заголовок карточки |
| | Кадастровый номер | 👁️ **Видно** | Номер в блоке характеристик |
| | Категория | 👁️ **Видно** | Цвет участка и строчка в легенде |
| | Площадь / Высота 3D | 👁️ **Видно** | Строка «Площадь: ... м²» и объём здания |
| **2. Владение** | **Собственник / Контакты** | 🔒 **СКРЫТО** | **Не выводится.** Данные остаются только в админке |
| | **Список арендаторов** | 🔒 **СКРЫТО** | **Не выводится.** Полная конфиденциальность |
| **3. Статусы** | Флаги готовности | 👁️ **Видно** | Яркие бейджи (зелёный, жёлтый, синий) |
| **4. Медиа** | Фотографии и ссылки | 👁️ **Видно** | Удобная галерея снимков и кнопки перехода |
| **5. Доп. инфо** | Описание и характеристики | 👁️ **Видно** | Текст о лоте и табличка параметров |

:::shield Главное правило для менеджеров
Всё, что вносится во вкладку **«Владение»**, остаётся внутри компании. Всё, что добавляется в **«Описание»** и **«Характеристики»**, будет открыто для всех гостей сайта.
:::

---

## 3. Как работать с паспортом в панели администратора

Карточка открывается справа в трёх случаях:
1. Вы только что **закончили чертить** новый участок или дорогу.
2. Вы **кликнули мышкой** по объекту на карте.
3. Вы выбрали нужный объект в **таблице всех объектов**.

### Быстрые кнопки над объектом
Когда вы нажимаете на участок, прямо слева от карточки появляется маленькая панель управления:

* ✏️ **Перерисовать границы:** переводит контур в режим правки точек. Можно подвинуть забор или выровнять угол прямо на карте, при этом весь текст паспорта сохраняется.
* 🔄 **Кнопки подключаемых модулей:** если в системе активированы дополнительные расширения, здесь автоматически появляются их иконки быстрого доступа.
* 🗑️ **Удалить:** удаляет объект с карты. Система обязательно переспросит подтверждение, чтобы исключить случайный клик.

При нажатии кнопки **«Сохранить»** изменения моментально применяются на карте, а карта возвращается к комфортному общему виду.

---

## 4. Как карточку видит покупатель на публичной карте

Для внешних гостей паспорт превращается в красивую презентационную витрину:

1. **Плавный подлёт:** при нажатии на участок карта мягко центрируется на нём за 1 секунду.
2. **Прямая ссылка на объект:** чтобы скопировать ссылку на конкретный участок для клиента, откройте этот участок в **пользовательском (публичном) режиме**. Адресная строка браузера автоматически примет вид \`.../map/?feature=ID\`. Скопируйте этот URL и отправьте в WhatsApp или Telegram: клиент перейдёт по ссылке, карта сама подлетит к участку крупным планом и откроет его фотогалерею с характеристиками.
3. **Возврат на место:** когда гость закрывает карточку крестиком или клавишей \`Esc\`, камера автоматически возвращается в ту же точку карты, где он находился до этого.
`
};
